import React, { useRef, useState } from 'react'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage } from '@/utils/layer'
import { Formik, Form, FormikProps } from 'formik'
import { Column } from '@/components/layout/layout'
import FormItem from '../../../../components/form/form-item/form-item'
import Input from '@/components/input/input'
import { checkError, getErrorStr } from '@/utils/form'
import { getBEMCls } from '@/utils/classname'
import Button from '@/components/button/button'
import { useRequest, useMount } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { Spin } from 'antd'

interface ISupplierForm {
    name: string
    phone: string
}

interface ISupplierFormError {
    name?: string
    phone?: string
}

interface PurchaseSupplierFormProps {
    refresh: () => void
    supplierId?: number
}

const PurchaseSupplierForm: React.FC<PurchaseSupplierFormProps> = props => {
    const cls = (e?: string, m?: string) => getBEMCls('purchase-supplier', e, m)
    const formRef = useRef<FormikProps<ISupplierForm>>(null)
    const [loading, setLoading] = useState<boolean>(() => !!props.supplierId)

    const initVal: ISupplierForm = {
        name: '',
        phone: '',
    }

    const createReq = useRequest(
        (val: ISupplierForm) =>
            $post({
                url: props.supplierId ? '/warehouse/editsupplier' : '/warehouse/createsupplier',
                params: {
                    ...val,
                    supplier_id: props.supplierId,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                props.refresh()
                layerManage.pop()
                $msg('success', `${props.supplierId ? '更新' : '添加'}成功`)
            },
        },
    )

    const getSupplierInfo = useRequest(
        () =>
            $post({
                url: '/warehouse/getshopsupplierdetail',
                params: {
                    limit: 20,
                    page: 1,
                    supplier_id: props.supplierId,
                },
            }),
        {
            manual: true,
            onSuccess: res => {
                if (formRef.current) {
                    formRef.current!.setValues(res.supplier_info)
                }
                setLoading(false)
            },
        },
    )

    useMount(() => {
        props.supplierId && getSupplierInfo.run()
    })

    const onSubmit = (val: ISupplierForm) => {
        createReq.run(val)
    }

    const validateVal = (val: ISupplierForm) => {
        const error: ISupplierFormError = {}

        if (!val.name.trim().length) {
            error.name = '请输入供货商名称'
        }

        if (val.phone.trim().length && (!/^1\d{2,11}$/.test(val.phone) || val.phone.trim().length < 11)) {
            error.phone = '请输入正确格式的手机号'
        }

        return error
    }

    return (
        <BaseDialog
            isShow
            title="添加供货商"
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Formik
                onSubmit={onSubmit}
                validateOnBlur
                initialValues={initVal}
                validate={validateVal}
                innerRef={formRef}
            >
                {({ values, handleChange, handleBlur, errors, touched }) => (
                    <Form>
                        <Spin spinning={loading}>
                            <Column className={cls('form-container')}>
                                <FormItem
                                    titleWidth={72}
                                    isRequire
                                    title="供货商名称"
                                    error={getErrorStr('name', errors, touched)}
                                >
                                    <Input
                                        error={checkError('name', errors, touched)}
                                        name="name"
                                        maxLength={10}
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="请输入供货商名称"
                                        inputsuffix={() => (
                                            <span data-font-size-14 data-color-000>
                                                {values.name.length}/10
                                            </span>
                                        )}
                                    />
                                </FormItem>
                                <FormItem
                                    titleWidth={72}
                                    title="联系电话"
                                    error={getErrorStr('phone', errors, touched)}
                                >
                                    <Input
                                        error={checkError('phone', errors, touched)}
                                        name="phone"
                                        maxLength={11}
                                        value={values.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="请输入供货商联系方式"
                                        inputsuffix={() => (
                                            <span data-font-size-14 data-color-000>
                                                {values.phone.length}/11
                                            </span>
                                        )}
                                    />
                                </FormItem>
                                <div className={cls('footer')}>
                                    <div
                                        data-cursor-pointer
                                        className={cls('footer', 'cancel')}
                                        onClick={() => {
                                            layerManage.pop()
                                        }}
                                    >
                                        取消
                                    </div>
                                    <Button loading={createReq.loading || void 0} types="default" type="submit">
                                        确定
                                    </Button>
                                </div>
                            </Column>
                        </Spin>
                    </Form>
                )}
            </Formik>
        </BaseDialog>
    )
}

export default PurchaseSupplierForm
